import { graphql, useLazyLoadQuery } from 'react-relay';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { useIsReviewNudgePageEligibleQuery } from './__generated__/useIsReviewNudgePageEligibleQuery.graphql.ts';

// The `generatedBy` content property Rovo Smart Create writes (also read by the agent trust receipt).
const ROVO_SMART_CREATION_GENERATED_BY = 'ROVO_SMART_CREATION';

/**
 * Whether the page suits the Review button nudges: the current user owns it or has contributed to it,
 * and it wasn't created with Rovo. A new draft may not list its owner as a contributor yet, so owner
 * is checked separately. Suspends while loading and throws on error, so render it inside a
 * `Suspense` and an error boundary.
 */
export const useIsReviewNudgePageEligible = (contentId: string): boolean => {
	const { userId } = useSessionData();
	const data = useLazyLoadQuery<useIsReviewNudgePageEligibleQuery>(
		graphql`
			query useIsReviewNudgePageEligibleQuery($contentId: ID!) {
				content(id: $contentId, status: ["current", "draft"]) {
					nodes {
						id
						history {
							ownedBy {
								... on KnownUser {
									accountId
								}
							}
						}
						contentProperties {
							draft {
								generatedBy
							}
						}
					}
				}
				contentContributors(id: $contentId) {
					isCurrentUserContributor
				}
			}
		`,
		{ contentId },
	);

	// A published page with an open draft can return both a current and a draft node, in no
	// guaranteed order, so check every node rather than the first.
	const nodes = data.content?.nodes ?? [];
	// Both ids are required, otherwise an anonymous viewer would match an unknown owner.
	const isOwner = Boolean(
		userId && nodes.some((node) => node?.history?.ownedBy?.accountId === userId),
	);
	const isContributor = data.contentContributors?.isCurrentUserContributor ?? false;
	const isCreatedWithRovo = nodes.some(
		(node) => node?.contentProperties?.draft?.generatedBy === ROVO_SMART_CREATION_GENERATED_BY,
	);

	return (isOwner || isContributor) && !isCreatedWithRovo;
};
